<template>
  <div
    class="base-skeleton"
    :class="computedClass"
    :style="computedStyle"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
interface Props {
  width?: string | number
  height?: string | number
  form?: 'rectangle' | 'circle' | 'rounded' | 'text'
  borderRadius?: string
  animation?: boolean
  display?: 'block' | 'inline-block' | 'flex' | 'inline-flex'
  direction?: 'row' | 'column'
  justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around'
  size?: 'sm' | 'sm-alt' | 'md' | 'lg'
}

const props = withDefaults(defineProps<Props>(), {
  form: 'rectangle',
  animation: true,
  display: 'inline-block',
  direction: 'row',
  size: 'sm',
  justify: 'flex-start',
})

const computedClass = computed(() => ({
  [`base-skeleton--form-${props.form}`]: true,
  [`base-skeleton--display-${props.display}`]: true,
  [`base-skeleton--direction-${props.direction}`]: true,
  [`base-skeleton--size-${props.size}`]: true,
  'base-skeleton--with-animation': props.animation,
}))

const computedStyle = computed(() => {
  const style: Record<string, string> = {
    justifyContent: props.justify,
  }

  if (props.width) {
    style.width = typeof props.width === 'number' ? `${props.width}px` : props.width
  }

  if (props.height) {
    style.height = typeof props.height === 'number' ? `${props.height}px` : props.height
  }

  if (props.borderRadius) {
    style.borderRadius = props.borderRadius
  }

  return style
})
</script>

<style lang="scss" scoped>
.base-skeleton {
  background: linear-gradient(90deg, var(--skeleton-from) 25%, var(--skeleton-via) 50%, var(--skeleton-to) 75%);
  background-size: 200% 100%;
  position: relative;
  overflow: hidden;

  &--with-animation {
    animation: skeleton-loading 1s infinite linear;
  }

  &--display {
    &-block {
      display: block;
    }

    &-inline-block {
      display: inline-block;
    }

    &-flex {
      display: flex;
    }

    &-inline-flex {
      display: inline-flex;
    }
  }

  &--form {
    &-rectangle {
      border-radius: 0;
    }

    &-circle {
      border-radius: 50%;
    }

    &-rounded {
      border-radius: var(--radius-sm, 4px);
    }

    &-text {
      border-radius: var(--radius-sm, 4px);
      height: 1em;
      transform: scale(1, 0.6);
      transform-origin: 0 60%;

      &::after {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
      }
    }
  }

  &--size {
    &-sm {
      padding: rem(8px);
      border-radius: var(--radius-sm);
      gap: rem(4px);
    }

    &-sm-alt {
      padding: rem(16px);
      border-radius: calc(var(--radius-sm) + 4px);
      gap: rem(8px);
    }

    &-md {
      padding: rem(20px);
      border-radius: var(--radius-md);
      gap: rem(10px);

      @include bp-media('xs', 'sm') {
        padding: rem(16px);
        gap: rem(8px);
        border-radius: calc(var(--radius-md) - 10px);
      }
    }

    &-lg {
      padding: rem(32px);
      border-radius: var(--radius-md);
      gap: rem(16px);

      @include bp-media('xs', 'sm') {
        padding: rem(24px);
        gap: rem(12px);
      }

      @include bp-media('xs') {
        padding: rem(16px);
        gap: rem(8px);
        border-radius: calc(var(--radius-md) - 10px);
      }
    }
  }

  &--direction {
    &-row {
      flex-direction: row;
    }

    &-column {
      flex-direction: column;
    }
  }

  .base-skeleton {
    background: linear-gradient(90deg, var(--skeleton-nested-from) 25%, var(--skeleton-nested-via) 50%, var(--skeleton-nested-to) 75%);
    background-size: 200% 100%;
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 100% 0;
  }

  100% {
    background-position: -100% 0;
  }
}
</style>
